import React, { useCallback, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from 'recharts';
import { isServiceRevenueRecognized } from '../../service/serviceBilling';

const Icon = ({ path, className = 'w-6 h-6' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const SummaryCard = ({
  title,
  value,
  helper,
  impact,
  iconPath,
  tone = 'slate',
  isActive = false,
  onClick,
}) => {
  const tones = {
    purple: {
      shell: 'border-violet-200 bg-[linear-gradient(145deg,rgba(245,243,255,0.98)_0%,rgba(255,255,255,1)_45%,rgba(250,245,255,0.98)_100%)] shadow-[0_18px_38px_rgba(109,40,217,0.08)]',
      icon: 'bg-violet-500/12 text-violet-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]',
      title: 'text-violet-700',
      value: 'text-slate-900',
      active: 'border-violet-400 bg-[linear-gradient(145deg,#7c3aed_0%,#9333ea_45%,#c026d3_100%)] text-white shadow-[0_24px_60px_rgba(124,58,237,0.28)]',
      helper: 'text-violet-200',
    },
    pink: {
      shell: 'border-pink-200 bg-[linear-gradient(145deg,rgba(253,242,248,0.98)_0%,rgba(255,255,255,1)_45%,rgba(255,241,242,0.98)_100%)] shadow-[0_18px_38px_rgba(219,39,119,0.08)]',
      icon: 'bg-pink-500/12 text-pink-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]',
      title: 'text-pink-700',
      value: 'text-slate-900',
      active: 'border-pink-400 bg-[linear-gradient(145deg,#db2777_0%,#e11d48_50%,#f43f5e_100%)] text-white shadow-[0_24px_60px_rgba(225,29,72,0.24)]',
      helper: 'text-pink-100',
    },
    green: {
      shell: 'border-emerald-200 bg-[linear-gradient(145deg,rgba(236,253,245,0.98)_0%,rgba(255,255,255,1)_45%,rgba(240,253,244,0.98)_100%)] shadow-[0_18px_38px_rgba(5,150,105,0.08)]',
      icon: 'bg-emerald-500/12 text-emerald-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]',
      title: 'text-emerald-700',
      value: 'text-slate-900',
      active: 'border-emerald-400 bg-[linear-gradient(145deg,#059669_0%,#10b981_50%,#22c55e_100%)] text-white shadow-[0_24px_60px_rgba(16,185,129,0.24)]',
      helper: 'text-emerald-100',
    },
    red: {
      shell: 'border-rose-200 bg-[linear-gradient(145deg,rgba(255,241,242,0.98)_0%,rgba(255,255,255,1)_45%,rgba(254,242,242,0.98)_100%)] shadow-[0_18px_38px_rgba(225,29,72,0.08)]',
      icon: 'bg-rose-500/12 text-rose-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]',
      title: 'text-rose-700',
      value: 'text-slate-900',
      active: 'border-rose-400 bg-[linear-gradient(145deg,#dc2626_0%,#e11d48_55%,#fb7185_100%)] text-white shadow-[0_24px_60px_rgba(225,29,72,0.24)]',
      helper: 'text-rose-100',
    },
  };

  const palette = tones[tone] || tones.green;
  const impactAmount = typeof impact === 'object' ? impact?.amount : impact;
  const impactLabel = typeof impact === 'object' ? impact?.label : '';
  const impactDetail = typeof impact === 'object' ? impact?.detail : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative w-full overflow-hidden rounded-[28px] border p-5 text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
        isActive ? palette.active : palette.shell
      }`}
    >
      <div className="pointer-events-none absolute inset-0">
        <div className={`absolute inset-x-0 top-0 h-px ${isActive ? 'bg-white/30' : 'bg-white/90'}`} />
        <div className={`absolute -right-10 -top-10 h-36 w-36 rounded-full blur-3xl transition-opacity duration-300 ${isActive ? 'bg-white/20 opacity-100' : 'bg-white/70 opacity-70 group-hover:opacity-100'}`} />
        <div className={`absolute -left-12 bottom-0 h-28 w-28 rounded-full blur-3xl transition-opacity duration-300 ${isActive ? 'bg-white/10 opacity-80' : 'bg-slate-100 opacity-60 group-hover:opacity-90'}`} />
      </div>

      <div className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <div className="absolute inset-x-8 top-0 h-20 bg-[linear-gradient(180deg,rgba(255,255,255,0.35),rgba(255,255,255,0))] blur-2xl" />
      </div>

      <div className="relative z-10 flex items-start justify-between gap-4">
        <div>
          <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${isActive ? 'text-white/75' : palette.title}`}>
            {title}
          </p>
          <p className={`mt-3 text-3xl font-black tracking-[-0.04em] ${isActive ? 'text-white' : palette.value}`}>
            {value}
          </p>
          {helper ? (
            <p className={`mt-2 text-sm ${isActive ? palette.helper : 'text-slate-500'}`}>
              {helper}
            </p>
          ) : null}
          {impactAmount ? (
            <div className={`mt-3 rounded-2xl border px-3 py-2 ${isActive ? 'border-white/20 bg-white/10' : 'border-slate-200 bg-white/70'}`}>
              <p className={`text-[11px] font-bold uppercase tracking-[0.18em] ${isActive ? 'text-white/75' : 'text-slate-400'}`}>
                Último impacto
              </p>
              <p className={`mt-1 text-sm font-black ${isActive ? 'text-white' : 'text-slate-800'}`}>
                {impactAmount}
              </p>
              {impactLabel ? (
                <p className={`mt-1 text-xs font-semibold ${isActive ? 'text-white/80' : 'text-slate-600'}`}>
                  {impactLabel}
                </p>
              ) : null}
              {impactDetail ? (
                <p className={`mt-0.5 text-[11px] ${isActive ? 'text-white/65' : 'text-slate-500'}`}>
                  {impactDetail}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${isActive ? 'bg-white/15 text-white' : palette.icon}`}>
          <Icon path={iconPath} className="h-6 w-6" />
        </div>
      </div>
    </button>
  );
};

const ChartShell = ({ eyebrow, title, description, children }) => (
  <section className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm md:p-6">
    <div className="mb-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{eyebrow}</p>
      <h4 className="mt-2 text-xl font-bold tracking-[-0.03em] text-slate-900">{title}</h4>
      {description ? <p className="mt-2 text-sm leading-relaxed text-slate-500">{description}</p> : null}
    </div>
    {children}
  </section>
);

const EmptyState = ({ title, description }) => (
  <div className="flex h-[280px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-300 bg-slate-50 px-6 text-center">
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
      <Icon
        path="M19.5 14.25v-2.625a3.375 3.375 0 00-1.028-2.426L13.5 4.5a3.375 3.375 0 00-2.386-.99H6.75A2.25 2.25 0 004.5 5.76v12.48a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25z"
        className="h-7 w-7 text-slate-500"
      />
    </div>
    <h5 className="mt-5 text-lg font-semibold text-slate-800">{title}</h5>
    <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500">{description}</p>
  </div>
);

const formatDayLabel = (value) =>
  new Intl.DateTimeFormat('es-AR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(value);

const formatDateTimeLabel = (value) =>
  new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(value);

const routeClosedAtDate = (route = {}) => {
  const candidates = [
    route?.closedAt,
    route?.finishedAt,
    route?.completedAt,
    route?.updatedAt,
    route?.startedAt,
    route?.date ? `${route.date}T00:00:00` : null,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate?.toDate === 'function') {
      try {
        return candidate.toDate();
      } catch {
        continue;
      }
    }
    const parsed = candidate instanceof Date ? candidate : new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
};

const groupLedgerRowsByDay = (rows = []) => {
  const map = new Map();

  rows
    .filter((row) => row?.date instanceof Date && !Number.isNaN(row.date.getTime()))
    .sort((a, b) => b.date - a.date)
    .forEach((row) => {
      const dayKey = row.date.toISOString().slice(0, 10);
      if (!map.has(dayKey)) {
        map.set(dayKey, {
          key: dayKey,
          date: row.date,
          label: formatDayLabel(row.date),
          total: 0,
          rows: [],
        });
      }

      const bucket = map.get(dayKey);
      bucket.total += Number(row.amount || 0);
      bucket.rows.push(row);
    });

  return Array.from(map.values());
};

const LedgerDayAccordion = ({
  groups = [],
  formatCurrency,
  tone = 'slate',
  emptyTitle,
  emptyDescription,
}) => {
  const [openDay, setOpenDay] = useState(groups[0]?.key || null);

  React.useEffect(() => {
    if (!groups.length) {
      setOpenDay(null);
      return;
    }
    if (!groups.some((group) => group.key === openDay)) {
      setOpenDay(groups[0].key);
    }
  }, [groups, openDay]);

  const tones = {
    expenses: {
      shell: 'border-rose-200 bg-rose-50/60',
      badge: 'border-rose-200 bg-white text-rose-700',
      amount: 'text-rose-800',
    },
    sales: {
      shell: 'border-violet-200 bg-violet-50/60',
      badge: 'border-violet-200 bg-white text-violet-700',
      amount: 'text-violet-800',
    },
    difference: {
      shell: 'border-emerald-200 bg-emerald-50/60',
      badge: 'border-emerald-200 bg-white text-emerald-700',
      amount: 'text-emerald-800',
    },
    slate: {
      shell: 'border-slate-200 bg-slate-50/60',
      badge: 'border-slate-200 bg-white text-slate-700',
      amount: 'text-slate-800',
    },
  };

  const palette = tones[tone] || tones.slate;

  if (!groups.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} />;
  }

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const isOpen = openDay === group.key;
        return (
          <div key={group.key} className={`overflow-hidden rounded-[24px] border ${palette.shell}`}>
            <button
              type="button"
              onClick={() => setOpenDay((current) => (current === group.key ? null : group.key))}
              className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
            >
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{group.label}</p>
                <p className={`mt-1 text-lg font-black ${palette.amount}`}>$ {formatCurrency(group.total)}</p>
                <p className="mt-1 text-xs text-slate-500">{group.rows.length} movimientos en el día</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${palette.badge}`}>
                  {isOpen ? 'Ocultar' : 'Ver detalle'}
                </span>
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
                  <Icon path={isOpen ? 'M19.5 14.25l-7.5-7.5-7.5 7.5' : 'M4.5 9.75l7.5 7.5 7.5-7.5'} className="h-4 w-4" />
                </div>
              </div>
            </button>
            {isOpen ? (
              <div className="border-t border-white/70 bg-white/80 px-4 py-3">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Hora</th>
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Origen</th>
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Detalle</th>
                        <th className="py-3 pr-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Referencia</th>
                        <th className="py-3 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {group.rows.map((row) => (
                        <tr key={row.id} className="align-top">
                          <td className="py-3 pr-3 text-sm font-medium text-slate-700">{formatDateTimeLabel(row.date)}</td>
                          <td className="py-3 pr-3 text-sm font-semibold text-slate-900">{row.origin}</td>
                          <td className="py-3 pr-3 text-sm text-slate-600">{row.detail}</td>
                          <td className="py-3 pr-3 text-sm text-slate-500">{row.reference || 'Sin referencia'}</td>
                          <td className={`py-3 text-right text-sm font-black ${palette.amount}`}>
                            {row.sign === 'negative' ? '-' : '+'}$ {formatCurrency(Math.abs(Number(row.amount || 0)))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default function BalanceGeneralView({
  allRoutes,
  allServices,
  allFleetExpenses,
  stockReportData,
  settings,
  onShowHistory,
  fleetCategories,
  calculateRouteCost,
  getOfficeLocationFromSettings,
  getRouteTotalDistance,
  formatCurrency,
  AnnualExpenseDetailView,
  DetailedTransporteView,
  DetailedFlotaView,
  DetailedKmsView,
}) {
  const [period, setPeriod] = useState('month');
  const [activePieIndex, setActivePieIndex] = useState(0);
  const [detailedView, setDetailedView] = useState(null);
  const [showAnnualExpenseDetail, setShowAnnualExpenseDetail] = useState(false);
  const [showUnifiedBusinessBalance, setShowUnifiedBusinessBalance] = useState(false);
  const [hasViewedUnifiedBusinessBalance, setHasViewedUnifiedBusinessBalance] = useState(false);
  const [unifiedDetailView, setUnifiedDetailView] = useState('expenses');

  const onPieEnter = useCallback((_, index) => {
    setActivePieIndex(index);
  }, []);

  const {
    costoRutas,
    gastosFlota,
    egresoTotal,
    stockPurchaseSpend,
    stockSpecialValue,
    stockSalesTotal,
    serviceSalesTotal,
    unifiedSalesTotal,
    stockNetDifference,
    globalExpenseTotal,
    totalKm,
    pieChartData,
    barChartData,
    frequentDestinations,
    periodLabel,
    detailedTransporteData,
    detailedFlotaData,
    detailedKmsData,
    movementsCount,
    lastExpenseImpact,
    lastServiceImpact,
    lastDifferenceImpact,
    expenseGroups,
    salesGroups,
    differenceGroups,
  } = useMemo(() => {
    const now = new Date();
    const officeLocation = getOfficeLocationFromSettings(settings);
    let routesForPeriod;
    let expensesForPeriod;
    let servicesForPeriod;
    let localPeriodLabel = '';
    let periodStartDate = null;

    const toDate = (value) => {
      if (!value) return null;
      if (typeof value?.toDate === 'function') {
        try {
          return value.toDate();
        } catch {
          return null;
        }
      }
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const serviceDate = (service = {}) => (
      toDate(service?.recognizedAt) ||
      toDate(service?.revenueRecognizedAt) ||
      toDate(service?.financialClosedAt) ||
      toDate(service?.completedAt) ||
      toDate(service?.paidAt) ||
      toDate(service?.invoicedAt) ||
      toDate(service?.scheduledDate) ||
      toDate(service?.date) ||
      toDate(service?.createdAt)
    );

    const isServiceClosed = (service = {}) => {
      const normalizedStatus = String(service?.status || '').trim().toLowerCase();
      return ['completed', 'invoiced', 'paid'].includes(normalizedStatus);
    };

    const isServiceRecognizedForBalance = (service = {}) =>
      isServiceClosed(service) && isServiceRevenueRecognized(service);

    switch (period) {
      case 'week': {
        const currentDate = new Date();
        const firstDayOfWeek =
          currentDate.getDate() - currentDate.getDay() + (currentDate.getDay() === 0 ? -6 : 1);
        const startDate = new Date(currentDate.setDate(firstDayOfWeek));
        startDate.setHours(0, 0, 0, 0);
        periodStartDate = startDate;

        routesForPeriod = allRoutes.filter((r) => new Date(`${r.date}T00:00:00`) >= startDate);
        expensesForPeriod = allFleetExpenses.filter((e) => new Date(`${e.date}T00:00:00`) >= startDate);
        servicesForPeriod = (allServices || []).filter((service) => {
          const date = serviceDate(service);
          return date && date >= startDate && isServiceRecognizedForBalance(service);
        });

        const monthName = startDate.toLocaleString('es-AR', { month: 'long' });
        localPeriodLabel = `Semana actual · ${monthName.charAt(0).toUpperCase() + monthName.slice(1)}`;
        break;
      }

      case 'year': {
        routesForPeriod = allRoutes;
        expensesForPeriod = allFleetExpenses;
        servicesForPeriod = (allServices || []).filter((service) => isServiceRecognizedForBalance(service));
        localPeriodLabel = 'Acumulado histórico';
        periodStartDate = new Date(0);
        break;
      }

      case 'month':
      default: {
        const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        periodStartDate = startDate;

        routesForPeriod = allRoutes.filter((r) => new Date(`${r.date}T00:00:00`) >= startDate);
        expensesForPeriod = allFleetExpenses.filter((e) => new Date(`${e.date}T00:00:00`) >= startDate);
        servicesForPeriod = (allServices || []).filter((service) => {
          const date = serviceDate(service);
          return date && date >= startDate && isServiceRecognizedForBalance(service);
        });

        localPeriodLabel = `Mes de ${now.toLocaleString('es-AR', { month: 'long' })}`;
        break;
      }
    }

    const costoRutas = routesForPeriod.reduce((sum, route) => {
      const cost = calculateRouteCost({ route, settings, officeLocation }).totalCost;
      return sum + (Number(cost) || 0);
    }, 0);

    const gastosFlota = expensesForPeriod.reduce((sum, expense) => sum + expense.amount, 0);
    const egresoTotal = costoRutas + gastosFlota;
    const stockProducts = Array.isArray(stockReportData?.products) ? stockReportData.products : [];
    const stockPurchaseLines = Array.isArray(stockReportData?.purchaseLines) ? stockReportData.purchaseLines : [];
    const stockMovements = Array.isArray(stockReportData?.movements) ? stockReportData.movements : [];
    const totalKm = routesForPeriod.reduce(
      (sum, route) => sum + parseFloat(getRouteTotalDistance(route, settings) || 0),
      0
    );

    const getMovementTime = (movement = {}) => {
      const sources = [movement.appliedAt, movement.createdAt, movement.updatedAt];
      for (const source of sources) {
        if (!source) continue;
        if (typeof source?.toDate === 'function') {
          try {
            return source.toDate().getTime();
          } catch {
            continue;
          }
        }
        const parsed = new Date(source);
        if (!Number.isNaN(parsed.getTime())) return parsed.getTime();
      }
      return 0;
    };

    const latestPurchaseCostByProductId = {};
    const purchaseLineById = {};

    stockPurchaseLines.forEach((line) => {
      const productId = String(line?.productId || '').trim();
      const lineId = String(line?.id || '').trim();
      if (lineId) purchaseLineById[lineId] = line;
      if (!productId) return;
      const unitCost = Number(line?.unitCost || 0);
      if (unitCost <= 0) return;
      const currentTime = Math.max(
        Number(line?.updatedAt?.seconds || 0) * 1000,
        Number(line?.createdAt?.seconds || 0) * 1000,
        line?.updatedAt instanceof Date ? line.updatedAt.getTime() : 0,
        line?.createdAt instanceof Date ? line.createdAt.getTime() : 0,
        Number.isNaN(new Date(line?.updatedAt).getTime()) ? 0 : new Date(line?.updatedAt).getTime(),
        Number.isNaN(new Date(line?.createdAt).getTime()) ? 0 : new Date(line?.createdAt).getTime()
      );
      const previous = latestPurchaseCostByProductId[productId];
      if (!previous || currentTime >= previous.timestamp) {
        latestPurchaseCostByProductId[productId] = { unitCost, timestamp: currentTime };
      }
    });

    stockMovements.forEach((movement) => {
      if (String(movement?.movementType || '') !== 'purchase_received') return;
      const productId = String(movement?.productId || '').trim();
      if (!productId) return;
      const unitCost = Number(movement.costUnitSnapshot || movement.unitCostSnapshot || movement.unitCost || 0);
      if (unitCost <= 0) return;
      const timestamp = getMovementTime(movement);
      const previous = latestPurchaseCostByProductId[productId];
      if (!previous || timestamp >= previous.timestamp) {
        latestPurchaseCostByProductId[productId] = { unitCost, timestamp };
      }
    });

    const resolveStockMovementCommercialSnapshot = (movement = {}) => {
      const quantity = Number(movement.quantity || 0) || 0;
      const productId = String(movement?.productId || '').trim();
      const sourcePurchaseLine = purchaseLineById[String(movement?.sourceLineId || movement?.purchaseLineId || '')] || null;
      const fallbackPurchaseUnitCost =
        Number(sourcePurchaseLine?.unitCost || 0) ||
        Number(latestPurchaseCostByProductId[productId]?.unitCost || 0) ||
        0;
      const snapshotCostUnit = Number(movement.costUnitSnapshot || movement.unitCostSnapshot || movement.unitCost || 0);
      const resolvedCostUnit = snapshotCostUnit > 0 ? snapshotCostUnit : fallbackPurchaseUnitCost;
      const saleUnit = Number(movement.saleUnitSnapshot || 0);
      const costTotalSnapshot = Number(movement.costTotalSnapshot || 0);
      const saleTotalSnapshot = Number(movement.saleTotalSnapshot || 0);
      const resolvedCostTotal = costTotalSnapshot > 0 ? costTotalSnapshot : resolvedCostUnit * quantity;
      const resolvedSaleTotal = saleTotalSnapshot > 0 ? saleTotalSnapshot : saleUnit * quantity;
      const snapshotMargin = Number(movement.marginSnapshot || 0);
      const resolvedMargin =
        snapshotMargin !== 0
          ? snapshotMargin
          : resolvedSaleTotal - resolvedCostTotal;

      return {
        quantity,
        costUnit: resolvedCostUnit,
        saleUnit,
        costTotal: resolvedCostTotal,
        saleTotal: resolvedSaleTotal,
        margin: resolvedMargin,
      };
    };

    const periodStockSummary = stockMovements.reduce((acc, movement) => {
      const timestamp = getMovementTime(movement);
      if (!periodStartDate || timestamp < periodStartDate.getTime()) return acc;

      const { costTotal, saleTotal } = resolveStockMovementCommercialSnapshot(movement);
      const quantity = Number(movement.quantity || 0);
      const sourcePurchaseLine = purchaseLineById[String(movement?.sourceLineId || movement?.purchaseLineId || '')] || null;
      const fallbackPurchaseUnitCost = Number(sourcePurchaseLine?.unitCost || 0);
      const fallbackPurchaseCostTotal = fallbackPurchaseUnitCost > 0 ? fallbackPurchaseUnitCost * quantity : 0;

      if (String(movement?.movementType || '') === 'purchase_received') {
        acc.purchaseSpend += costTotal > 0 ? costTotal : fallbackPurchaseCostTotal;
      }

      if (String(movement?.movementType || '') === 'stock_outbound_delivered') {
        acc.salesTotal += saleTotal;
      }

      return acc;
    }, { purchaseSpend: 0, salesTotal: 0 });

    const stockSpecialValue = stockProducts.reduce((acc, product) => {
      const productId = String(product?.id || '').trim();
      const buckets = product?.stockBuckets || {};
      const specialQty =
        Math.max(0, Number(buckets.repairQty || 0)) +
        Math.max(0, Number(buckets.returnedQty || 0)) +
        Math.max(0, Number(buckets.damagedQty || 0));
      if (specialQty <= 0) return acc;
      const resolvedCost =
        Number(latestPurchaseCostByProductId[productId]?.unitCost || 0) ||
        Number(product?.defaultPurchaseCost || 0);
      return acc + specialQty * Math.max(0, resolvedCost);
    }, 0);

    const stockPurchaseSpend = periodStockSummary.purchaseSpend;
    const stockSalesTotal = periodStockSummary.salesTotal;
    const serviceSalesTotal = servicesForPeriod.reduce(
      (acc, service) => acc + Math.max(0, Number(service?.recognizedSalePrice ?? service?.realizedSalePrice ?? service?.salePrice ?? 0)),
      0
    );
    const unifiedSalesTotal = stockSalesTotal + serviceSalesTotal;
    const globalExpenseTotal = egresoTotal + stockPurchaseSpend + stockSpecialValue;
    const stockNetDifference = unifiedSalesTotal - globalExpenseTotal;

    const gastosFlotaPorCategoria = expensesForPeriod.reduce((acc, expense) => {
      const category = fleetCategories.find((c) => c.id === expense.type);
      const categoryName = category ? category.name : 'Otros';
      acc[categoryName] = (acc[categoryName] || 0) + expense.amount;
      return acc;
    }, {});

    const pieChartData = [
      ...(costoRutas > 0 ? [{ name: 'Pago Transportistas', value: costoRutas }] : []),
      ...Object.entries(gastosFlotaPorCategoria).map(([name, value]) => ({ name, value })),
    ].filter((item) => item.value > 0);

    const groupedData = {};

    const dataToGroup = [
      ...routesForPeriod.map((r) => ({
        ...r,
        itemType: 'route',
        itemDate: new Date(`${r.date}T00:00:00`),
      })),
      ...expensesForPeriod.map((e) => ({
        ...e,
        itemType: 'expense',
        itemDate: new Date(`${e.date}T00:00:00`),
      })),
    ];

    dataToGroup.forEach((item) => {
      const itemDate = item.itemDate;
      let key;

      if (period === 'year') {
        key = itemDate.getFullYear().toString();
      } else if (period === 'week') {
        key = itemDate.toLocaleDateString('es-AR', { weekday: 'short' });
      } else {
        const firstDayOfYear = new Date(itemDate.getFullYear(), 0, 1);
        const pastDaysOfYear = Math.floor((itemDate - firstDayOfYear) / 86400000);
        const weekNum = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
        key = `Sem ${weekNum}`;
      }

      if (!groupedData[key]) groupedData[key] = { name: key, Kilómetros: 0 };

      if (item.itemType === 'route') {
        const routeCost = calculateRouteCost({ route: item, settings, officeLocation }).totalCost;
        groupedData[key]['Pago Transportistas'] =
          (groupedData[key]['Pago Transportistas'] || 0) + routeCost;
        groupedData[key].Kilómetros += parseFloat(getRouteTotalDistance(item, settings) || 0);
      } else {
        const category = fleetCategories.find((c) => c.id === item.type);
        const categoryName = category ? category.name : 'Otros';
        groupedData[key][categoryName] = (groupedData[key][categoryName] || 0) + item.amount;
      }
    });

    const barData = Object.values(groupedData);
    if (period === 'year') {
      barData.sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));
    }

    const detailedTransporteData = routesForPeriod
      .map((item) => ({
        ...item,
        value: calculateRouteCost({ route: item, settings, officeLocation }).totalCost,
        reportDateTime: routeClosedAtDate(item),
      }))
      .filter((item) => item.value > 0);

    const detailedFlotaData = expensesForPeriod.map((item) => {
      const category = fleetCategories.find((c) => c.id === item.type);
      return {
        ...item,
        name: category ? category.name : 'Otros',
        value: item.amount,
        transportName: item.transportName || 'Unidad sin identificar',
        transportMeta: item.transportMeta || [item.transportLabel, item.transportModel, item.transportPatent].filter(Boolean).join(' · '),
        transportType: item.transportType || '',
        transportIconPath: item.transportIconPath || '',
        identityColor: item.identityColor || '',
        identitySoftColor: item.identitySoftColor || '',
        driverId: item.driverId || '',
        driverName: item.driverName || '',
        driverMeta: item.driverMeta || '',
        impactDriver: Boolean(item.impactDriver),
        categoryScopeLabel: item.categoryScopeLabel || category?.scopeLabel || '',
      };
    });

    const detailedKmsData = routesForPeriod
      .map((item) => ({
        ...item,
        value: parseFloat(getRouteTotalDistance(item, settings) || 0),
        reportDateTime: routeClosedAtDate(item),
      }))
      .filter((item) => item.value > 0);

    const destinationCounts = routesForPeriod
      .flatMap((r) => r.destinations || [])
      .filter((d) => d.status === 'completed' || d.status === 'incomplete')
      .reduce((acc, dest) => {
        acc[dest.name] = (acc[dest.name] || 0) + 1;
        return acc;
      }, {});

    const frequentDestinations = Object.entries(destinationCounts)
      .map(([name, visits]) => ({ name, visits }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10);

    const latestRouteExpense = routesForPeriod
      .map((route) => ({
        date: routeClosedAtDate(route),
        amount: Number(calculateRouteCost({ route, settings, officeLocation }).totalCost || 0) || 0,
        label: route?.name || 'Ruta',
        detail: route?.driverName || route?.transportName || route?.vehicleName || ''
      }))
      .filter((item) => item.date && item.amount > 0)
      .sort((a, b) => b.date - a.date)[0] || null;

    const latestFleetExpense = expensesForPeriod
      .map((expense) => ({
        date: toDate(expense?.date ? `${expense.date}T00:00:00` : null),
        amount: Number(expense?.amount || 0) || 0,
        label: expense?.transportName || expense?.description || 'Gasto de flota',
        detail: expense?.driverName || expense?.categoryScopeLabel || ''
      }))
      .filter((item) => item.date && item.amount > 0)
      .sort((a, b) => b.date - a.date)[0] || null;

    const latestExpenseImpact = [latestRouteExpense, latestFleetExpense]
      .filter(Boolean)
      .sort((a, b) => b.date - a.date)[0] || null;

    const latestStockSaleImpact = stockMovements
      .map((movement, index) => {
        const movementType = String(movement?.movementType || '');
        if (movementType !== 'stock_outbound_delivered') return null;

        const dateMs = getMovementTime(movement);
        if (!dateMs || (periodStartDate && dateMs < periodStartDate.getTime())) return null;

        const quantity = Number(movement.quantity || 0) || 0;
        const { saleTotal } = resolveStockMovementCommercialSnapshot(movement);
        if (saleTotal <= 0) return null;

        return {
          id: `stock-sale-${movement?.id || index}`,
          date: new Date(dateMs),
          amount: saleTotal,
          label: movement?.productNameSnapshot || movement?.productName || movement?.productId || 'Venta de stock',
          detail: `${quantity ? `${quantity} u.` : ''}${quantity ? ' · ' : ''}${movement?.routeNameSnapshot || movement?.orderId || 'Salida entregada'}`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.date - a.date)[0] || null;

    const latestServiceImpact = servicesForPeriod
      .map((service) => ({
        date: serviceDate(service),
        amount: Math.max(0, Number(service?.recognizedSalePrice ?? service?.realizedSalePrice ?? service?.salePrice ?? 0)),
        margin: Number(service?.recognizedMargin ?? service?.realizedMargin ?? service?.estimatedMargin ?? 0) || 0,
        label: service?.clientNameSnapshot || service?.clientName || 'Servicio',
        detail:
          service?.routeNameSnapshot ||
          service?.serviceReferenceCode ||
          service?.serviceModeLabel ||
          service?.serviceTaskLabel ||
          ''
      }))
      .filter((item) => item.date && item.amount > 0)
      .sort((a, b) => b.date - a.date)[0] || null;

    const latestSalesImpact = [latestStockSaleImpact, latestServiceImpact]
      .filter(Boolean)
      .sort((a, b) => b.date - a.date)[0] || null;

    const latestStockMarginImpact = stockMovements
      .map((movement, index) => {
        const movementType = String(movement?.movementType || '');
        if (movementType !== 'stock_outbound_delivered') return null;

        const dateMs = getMovementTime(movement);
        if (!dateMs || (periodStartDate && dateMs < periodStartDate.getTime())) return null;

        const quantity = Number(movement.quantity || 0) || 0;
        const { margin } = resolveStockMovementCommercialSnapshot(movement);
        if (margin === 0) return null;

        return {
          id: `stock-margin-${movement?.id || index}`,
          date: new Date(dateMs),
          amount: margin,
          margin,
          label: movement?.productNameSnapshot || movement?.productName || movement?.productId || 'Venta de stock',
          detail: movement?.routeNameSnapshot || movement?.orderId || 'Salida entregada',
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.date - a.date)[0] || null;

    const latestDifferenceImpact = [latestStockMarginImpact, latestServiceImpact ? {
      ...latestServiceImpact,
      amount: latestServiceImpact.margin,
    } : null]
      .filter(Boolean)
      .sort((a, b) => b.date - a.date)[0] || null;

    const expenseLedgerRows = [];
    const salesLedgerRows = [];
    const differenceLedgerRows = [];

    routesForPeriod.forEach((route, index) => {
      const amount = Number(calculateRouteCost({ route, settings, officeLocation }).totalCost || 0) || 0;
      const date = routeClosedAtDate(route);
      if (!date || amount <= 0) return;
      expenseLedgerRows.push({
        id: `route-${route?.id || route?.name || index}`,
        date,
        origin: 'Liquidación chofer',
        detail: route?.driverName || route?.vehicleName || route?.transportName || 'Ruta operativa',
        reference: route?.name || 'Ruta',
        amount,
        sign: 'negative',
      });
    });

    expensesForPeriod.forEach((expense, index) => {
      const amount = Number(expense?.amount || 0) || 0;
      const date = toDate(expense?.date ? `${expense.date}T00:00:00` : null);
      if (!date || amount <= 0) return;
      expenseLedgerRows.push({
        id: `fleet-${expense?.id || index}`,
        date,
        origin: expense?.categoryScopeLabel || 'Gasto de flota',
        detail: expense?.description || expense?.transportName || 'Egreso operativo',
        reference: [expense?.driverName, expense?.licensePlate].filter(Boolean).join(' · '),
        amount,
        sign: 'negative',
      });
    });

    stockMovements.forEach((movement, index) => {
      const movementType = String(movement?.movementType || '');
      const dateMs = getMovementTime(movement);
      if (!dateMs || (periodStartDate && dateMs < periodStartDate.getTime())) return;
      const date = new Date(dateMs);
      const quantity = Number(movement.quantity || 0) || 0;
      const productLabel = movement?.productNameSnapshot || movement?.productName || movement?.productId || 'Producto';
      const { costTotal, saleTotal, margin } = resolveStockMovementCommercialSnapshot(movement);

      if (movementType === 'purchase_received' && costTotal > 0) {
        expenseLedgerRows.push({
          id: `purchase-${movement?.id || index}`,
          date,
          origin: 'Compra de stock',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: movement?.purchaseId || movement?.sourceLineId || 'Ingreso de compra',
          amount: costTotal,
          sign: 'negative',
        });
      }

      if (movementType === 'stock_outbound_delivered' && saleTotal > 0) {
        salesLedgerRows.push({
          id: `sale-${movement?.id || index}`,
          date,
          origin: 'Venta de stock',
          detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
          reference: movement?.routeNameSnapshot || movement?.orderId || 'Salida entregada',
          amount: saleTotal,
          sign: 'positive',
        });

        const marginTotal = margin;
        if (marginTotal !== 0) {
          differenceLedgerRows.push({
            id: `stock-margin-${movement?.id || index}`,
            date,
            origin: 'Margen de stock',
            detail: `${productLabel}${quantity ? ` · ${quantity} u.` : ''}`,
            reference: movement?.routeNameSnapshot || movement?.orderId || 'Salida entregada',
            amount: marginTotal,
            sign: marginTotal >= 0 ? 'positive' : 'negative',
          });
        }
      }
    });

    servicesForPeriod.forEach((service, index) => {
      const date = serviceDate(service);
      const saleAmount = Math.max(0, Number(service?.recognizedSalePrice ?? service?.realizedSalePrice ?? service?.salePrice ?? 0));
      const marginAmount = Number(service?.recognizedMargin ?? service?.realizedMargin ?? service?.estimatedMargin ?? 0) || 0;
      if (!date || saleAmount <= 0) return;

      salesLedgerRows.push({
        id: `service-sale-${service?.id || index}`,
        date,
        origin: 'Venta de servicio',
        detail: service?.serviceTaskLabel || service?.serviceModeLabel || 'Servicio comercial',
        reference: service?.clientNameSnapshot || service?.clientName || service?.routeNameSnapshot || 'Servicio',
        amount: saleAmount,
        sign: 'positive',
      });

      if (marginAmount !== 0) {
        differenceLedgerRows.push({
          id: `service-margin-${service?.id || index}`,
          date,
          origin: 'Margen de servicio',
          detail: service?.serviceTaskLabel || service?.serviceModeLabel || 'Resultado comercial',
          reference: service?.clientNameSnapshot || service?.clientName || service?.routeNameSnapshot || 'Servicio',
          amount: marginAmount,
          sign: marginAmount >= 0 ? 'positive' : 'negative',
        });
      }
    });

    const expenseGroups = groupLedgerRowsByDay(expenseLedgerRows);
    const salesGroups = groupLedgerRowsByDay(salesLedgerRows);
    const differenceGroups = groupLedgerRowsByDay(differenceLedgerRows);

    return {
      costoRutas,
      gastosFlota,
      egresoTotal,
      stockPurchaseSpend,
      stockSpecialValue,
      stockSalesTotal,
      serviceSalesTotal,
      unifiedSalesTotal,
      stockNetDifference,
      globalExpenseTotal,
      totalKm,
      pieChartData,
      barChartData: barData,
      frequentDestinations,
      periodLabel: localPeriodLabel,
      detailedTransporteData,
      detailedFlotaData,
      detailedKmsData,
      movementsCount: routesForPeriod.length + expensesForPeriod.length,
      lastExpenseImpact: latestExpenseImpact,
      lastServiceImpact: latestSalesImpact,
      lastDifferenceImpact: latestDifferenceImpact,
      expenseGroups,
      salesGroups,
      differenceGroups,
    };
  }, [period, allRoutes, allServices, allFleetExpenses, stockReportData, settings, fleetCategories, calculateRouteCost, getOfficeLocationFromSettings, getRouteTotalDistance]);

  const PIE_COLORS = useMemo(() => {
    const colors = { 'Pago Transportistas': '#8b5cf6' };
    fleetCategories.forEach((cat) => {
      colors[cat.name] = cat.color;
    });
    colors.Otros = '#64748b';
    return colors;
  }, [fleetCategories]);

  const dominantExpense = useMemo(() => {
    const highest = [...pieChartData].sort((a, b) => b.value - a.value)[0];
    return highest?.name || 'Sin datos';
  }, [pieChartData]);

  const stockBalanceSummaryCards = useMemo(() => ([
    {
      key: 'expenses',
      title: 'Gastos',
      value: `$ ${formatCurrency(globalExpenseTotal)}`,
      helper: `Stock: compras $ ${formatCurrency(stockPurchaseSpend)} + incidentes $ ${formatCurrency(stockSpecialValue)} · Flota y liquidación $ ${formatCurrency(egresoTotal)}`,
      impact: lastExpenseImpact
        ? {
            amount: `-$ ${formatCurrency(lastExpenseImpact.amount)}`,
            label: lastExpenseImpact.label,
            detail: lastExpenseImpact.detail,
          }
        : null,
      iconPath: 'M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33',
      tone: 'red',
    },
    {
      key: 'sales',
      title: 'Ventas',
      value: `$ ${formatCurrency(unifiedSalesTotal)}`,
      helper: `Stock: $ ${formatCurrency(stockSalesTotal)} · Servicios: $ ${formatCurrency(serviceSalesTotal)}`,
      impact: lastServiceImpact
        ? {
            amount: `+$ ${formatCurrency(lastServiceImpact.amount)}`,
            label: lastServiceImpact.label,
            detail: lastServiceImpact.detail,
          }
        : null,
      iconPath: 'M3 13.5l6.75-6.75L14.25 11l6-6M19.5 7.5V13.5H13.5',
      tone: 'purple',
    },
    {
      key: 'difference',
      title: 'Diferencia',
      value: `${stockNetDifference < 0 ? '-$ ' : '$ '}${formatCurrency(Math.abs(stockNetDifference))}`,
      helper: 'Resultado simple: ventas menos gastos unificados del stock, la flota y la liquidación.',
      impact: lastDifferenceImpact
        ? {
            amount: `${Number(lastDifferenceImpact.margin || 0) >= 0 ? '+$ ' : '-$ '}${formatCurrency(Math.abs(Number(lastDifferenceImpact.margin || 0)))}`,
            label: lastDifferenceImpact.label,
            detail: lastDifferenceImpact.detail,
          }
        : null,
      iconPath: 'M7.5 15l4.5-4.5 4.5 4.5M7.5 9l4.5 4.5 4.5-4.5',
      tone: stockNetDifference >= 0 ? 'green' : 'red',
    },
  ]), [egresoTotal, formatCurrency, globalExpenseTotal, lastDifferenceImpact, lastExpenseImpact, lastServiceImpact, serviceSalesTotal, stockNetDifference, stockPurchaseSpend, stockSalesTotal, stockSpecialValue, unifiedSalesTotal]);

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill} className="font-bold text-lg">
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#334155">
          {`$ ${formatCurrency(value)}`}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#94a3b8"
        >
          {`(${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

  const toggleDetailedView = (view) => {
    setShowAnnualExpenseDetail(false);
    setDetailedView((prev) => (prev === view ? null : view));
  };

  const canOpenAnnualDetail = period === 'year';

  const handleAnnualCardClick = () => {
    if (!canOpenAnnualDetail) return;
    setDetailedView(null);
    setShowAnnualExpenseDetail((prev) => !prev);
  };

  const handleAnnualCardKeyDown = (event) => {
    if (!canOpenAnnualDetail) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAnnualCardClick();
    }
  };

  const hasMainData = pieChartData.length > 0 || barChartData.length > 0;

  return (
    <div className="space-y-6">
      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">
              Balance operativo
            </p>
            <h3 className="mt-3 text-3xl font-bold tracking-[-0.03em] text-slate-900 md:text-4xl">
              Lectura económica y territorial del período
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600 md:text-base">
              Visualizá pagos a transportistas, gastos de flota, kilómetros recorridos y concentración de destinos desde una sola vista ejecutiva.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Período activo</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">{periodLabel}</p>
            </div>

            <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1 shadow-sm">
              {['week', 'month', 'year'].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all ${
                    period === p
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  {p === 'week' ? 'Semana' : p === 'month' ? 'Mes' : 'Año'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-[30px] border border-cyan-100 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.98))] shadow-[0_22px_50px_rgba(15,23,42,0.08)]">
        <button
          type="button"
          onClick={() => {
            setShowUnifiedBusinessBalance((prev) => {
              const next = !prev;
              if (next) {
                setHasViewedUnifiedBusinessBalance(true);
              }
              return next;
            });
          }}
          className={`relative flex w-full items-center justify-between gap-4 overflow-hidden px-6 py-5 text-left transition-all duration-300 ${
            showUnifiedBusinessBalance
              ? 'border-b border-cyan-200 bg-[linear-gradient(135deg,#ecfeff_0%,#f8fafc_42%,#eef2ff_100%)] shadow-[inset_0_-1px_0_rgba(34,211,238,0.18),0_18px_40px_rgba(34,211,238,0.08)]'
              : 'bg-[linear-gradient(135deg,rgba(248,250,252,0.98)_0%,rgba(255,255,255,1)_48%,rgba(240,249,255,0.95)_100%)] hover:bg-slate-50 hover:shadow-[0_14px_36px_rgba(15,23,42,0.08)]'
          }`}
        >
          <div className="pointer-events-none absolute inset-0">
            <div className={`absolute inset-x-0 top-0 h-px ${showUnifiedBusinessBalance ? 'bg-white/80' : 'bg-cyan-100'}`} />
            <div className={`absolute -left-10 top-2 h-24 w-24 rounded-full blur-3xl transition-opacity duration-300 ${showUnifiedBusinessBalance ? 'bg-cyan-300/45 opacity-100' : 'bg-sky-200/30 opacity-80'}`} />
            <div className={`absolute right-0 top-0 h-full w-40 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.75),transparent_70%)] transition-opacity duration-300 ${showUnifiedBusinessBalance ? 'opacity-100' : 'opacity-60'}`} />
          </div>
          <div className="min-w-0">
            <p className={`text-[11px] font-semibold uppercase tracking-[0.28em] ${showUnifiedBusinessBalance ? 'text-cyan-700' : 'text-slate-500'}`}>
              Balance del negocio
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2">
              <h3 className={`text-xl font-black tracking-[-0.04em] md:text-[2rem] ${showUnifiedBusinessBalance ? 'text-slate-950' : 'text-slate-900'}`}>
                Gastos, ventas y diferencia
              </h3>
              <p className={`text-sm ${showUnifiedBusinessBalance ? 'text-slate-600' : 'text-slate-500'}`}>
                {showUnifiedBusinessBalance
                  ? 'Tenés a la vista el resultado general del período.'
                  : 'Abrí este panel para ver cómo viene el resultado del negocio.'}
              </p>
            </div>
            {showUnifiedBusinessBalance || !hasViewedUnifiedBusinessBalance ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                  showUnifiedBusinessBalance
                    ? 'border-cyan-200 bg-white/90 text-cyan-700 shadow-[0_10px_24px_rgba(34,211,238,0.12)]'
                    : 'border-amber-200 bg-amber-50 text-amber-700'
                }`}>
                  <span className={`h-2 w-2 rounded-full ${
                    showUnifiedBusinessBalance ? 'bg-cyan-500 animate-pulse' : 'bg-amber-500 animate-pulse'
                  }`} />
                  {showUnifiedBusinessBalance ? 'Resumen visible' : 'Revisar resultado'}
                </span>
              </div>
            ) : null}
          </div>
          <div className="relative z-10 flex shrink-0 items-center gap-3">
            <div className={`hidden rounded-full px-3 py-1 text-xs font-semibold md:inline-flex ${
              showUnifiedBusinessBalance
                ? 'border border-cyan-200 bg-white text-cyan-700 shadow-[0_8px_20px_rgba(34,211,238,0.12)]'
                : 'border border-slate-200 bg-white text-slate-600'
            }`}>
              {showUnifiedBusinessBalance ? 'Ocultar' : 'Ver resultado'}
            </div>
            <div className={`flex h-11 w-11 items-center justify-center rounded-[18px] transition-all duration-300 ${
              showUnifiedBusinessBalance
                ? 'bg-[linear-gradient(180deg,#0891b2_0%,#155e75_100%)] text-white shadow-[0_14px_30px_rgba(8,145,178,0.26)] rotate-0'
                : 'bg-slate-100 text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] -rotate-90'
            }`}>
              <Icon
                path={showUnifiedBusinessBalance ? 'M19.5 14.25l-7.5-7.5-7.5 7.5' : 'M4.5 9.75l7.5 7.5 7.5-7.5'}
                className="h-5 w-5"
              />
            </div>
          </div>
        </button>

        {showUnifiedBusinessBalance ? (
          <div className="border-t border-cyan-100 bg-[linear-gradient(180deg,rgba(236,254,255,0.55),rgba(255,255,255,0.92))] px-6 pb-6 pt-5 animate-[fadeIn_220ms_ease-out]">
            <p className="max-w-3xl text-sm leading-relaxed text-slate-600 md:text-base">
              Acá ves el resultado general del período: cuánto salió operar, cuánto entró por ventas y qué diferencia real te quedó. Después podés bajar al detalle de cada movimiento.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
              {stockBalanceSummaryCards.map((card) => (
                <SummaryCard
                  key={card.title}
                  title={card.title}
                  value={card.value}
                  helper={card.helper}
                  impact={card.impact}
                  iconPath={card.iconPath}
                  tone={card.tone}
                  isActive={unifiedDetailView === card.key}
                  onClick={() => setUnifiedDetailView(card.key)}
                />
              ))}
            </div>
            <div className="mt-5">
              {unifiedDetailView === 'expenses' ? (
                <LedgerDayAccordion
                  groups={expenseGroups}
                  formatCurrency={formatCurrency}
                  tone="expenses"
                  emptyTitle="Todavía no hay gastos desglosados"
                  emptyDescription="Cuando existan liquidaciones, compras o gastos de flota en el período, los vas a poder auditar por día desde acá."
                />
              ) : null}
              {unifiedDetailView === 'sales' ? (
                <LedgerDayAccordion
                  groups={salesGroups}
                  formatCurrency={formatCurrency}
                  tone="sales"
                  emptyTitle="Todavía no hay ventas desglosadas"
                  emptyDescription="Cuando existan ventas de stock o servicios cerrados en el período, las vas a ver agrupadas por día."
                />
              ) : null}
              {unifiedDetailView === 'difference' ? (
                <LedgerDayAccordion
                  groups={differenceGroups}
                  formatCurrency={formatCurrency}
                  tone="difference"
                  emptyTitle="Todavía no hay diferencia desglosada"
                  emptyDescription="Cuando los servicios tengan margen reconocido en el período, acá vas a poder seguir la diferencia real día por día."
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Pago transportistas"
          value={`$ ${formatCurrency(costoRutas)}`}
          helper="Detalle operativo por ruta"
          iconPath="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          tone="purple"
          isActive={detailedView === 'transporte'}
          onClick={() => toggleDetailedView('transporte')}
        />

        <SummaryCard
          title="Gastos de flota"
          value={`$ ${formatCurrency(gastosFlota)}`}
          helper="Consumo y egresos asociados"
          iconPath="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z"
          tone="pink"
          isActive={detailedView === 'flota'}
          onClick={() => toggleDetailedView('flota')}
        />

        <SummaryCard
          title="Kilómetros"
          value={`${totalKm.toFixed(2)} km`}
          helper="Recorrido acumulado en el período"
          iconPath="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25"
          tone="green"
          isActive={detailedView === 'kilometros'}
          onClick={() => toggleDetailedView('kilometros')}
        />

        <div
          role={canOpenAnnualDetail ? 'button' : undefined}
          tabIndex={canOpenAnnualDetail ? 0 : -1}
          onClick={handleAnnualCardClick}
          onKeyDown={handleAnnualCardKeyDown}
          className={`relative overflow-hidden rounded-[28px] border p-5 shadow-sm transition-all duration-300 ${
            canOpenAnnualDetail ? 'cursor-pointer hover:-translate-y-1 hover:shadow-xl' : ''
          } border-rose-200 bg-gradient-to-br from-rose-50 via-white to-red-50`}
        >
          <div className="relative z-10 flex h-full flex-col justify-between gap-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-rose-700">
                  Egreso total
                </p>
                <p className="mt-3 text-3xl font-black tracking-[-0.04em] text-slate-900">
                  $ {formatCurrency(egresoTotal)}
                </p>
                <p className="mt-2 text-sm text-slate-500">
                  {canOpenAnnualDetail ? 'Podés abrir el detalle anual.' : 'Disponible como lectura general del período.'}
                </p>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onShowHistory();
                }}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-600 text-white shadow-lg shadow-rose-200 transition hover:bg-rose-700"
                title="Ver historial anual"
              >
                <Icon
                  path="M12 6.042A8.967 8.967 0 003.75 12.55a8.967 8.967 0 008.25 6.508 8.967 8.967 0 008.25-6.508 8.967 8.967 0 00-8.25-6.508zM12 15a3 3 0 100-6 3 3 0 000 6z"
                  className="h-5 w-5"
                />
              </button>
            </div>

            {period === 'year' ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 shadow-sm">
                <Icon
                  path={showAnnualExpenseDetail ? 'M4.5 15.75l7.5-7.5 7.5 7.5' : 'M19.5 8.25l-7.5 7.5-7.5 7.5'}
                  className="h-4 w-4"
                />
                {showAnnualExpenseDetail ? 'Ocultar detalle' : 'Ver detalle anual'}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Movimientos</p>
          <p className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-900">{movementsCount}</p>
          <p className="mt-1 text-sm text-slate-500">Rutas y gastos considerados</p>
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Rubro principal</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{dominantExpense}</p>
          <p className="mt-1 text-sm text-slate-500">Mayor peso en la composición actual</p>
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Lectura activa</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{detailedView ? 'Detalle abierto' : 'Vista general'}</p>
          <p className="mt-1 text-sm text-slate-500">Usá las tarjetas superiores para profundizar</p>
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Destino líder</p>
          <p className="mt-2 text-lg font-bold text-slate-900">{frequentDestinations[0]?.name || 'Sin datos'}</p>
          <p className="mt-1 text-sm text-slate-500">Parada con más recurrencia</p>
        </div>
      </section>

      {showAnnualExpenseDetail && period === 'year' ? (
        <AnnualExpenseDetailView
          transporteData={detailedTransporteData}
          flotaData={detailedFlotaData}
          totalTransporte={costoRutas}
          totalFlota={gastosFlota}
        />
      ) : null}

      {detailedView ? (
        <section className="animate-fade-in">
          {detailedView === 'transporte' && (
            <DetailedTransporteView
              period={period}
              title="Detalle de pago a transportistas"
              data={detailedTransporteData}
              color={PIE_COLORS['Pago Transportistas']}
            />
          )}
          {detailedView === 'flota' && (
            <DetailedFlotaView
              title="Detalle de gastos de flota"
              data={detailedFlotaData}
              colors={PIE_COLORS}
            />
          )}
          {detailedView === 'kilometros' && (
            <DetailedKmsView
              title="Detalle de kilómetros recorridos"
              data={detailedKmsData}
              settings={settings}
            />
          )}
        </section>
      ) : (
        <section className="grid grid-cols-1 gap-6 xl:grid-cols-5 animate-fade-in">
          <div className="xl:col-span-2">
            <ChartShell
              eyebrow="Composición"
              title="Distribución de egresos"
              description="Visualizá cómo se reparte el gasto entre transportistas y categorías de flota."
            >
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={88}
                      activeIndex={activePieIndex}
                      activeShape={renderActiveShape}
                      onMouseEnter={onPieEnter}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[entry.name] || PIE_COLORS.Otros} />
                      ))}
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  title="Todavía no hay composición disponible"
                  description="Cuando existan rutas liquidadas o gastos cargados en el período, vas a ver la distribución acá."
                />
              )}
            </ChartShell>
          </div>

          <div className="xl:col-span-3">
            <ChartShell
              eyebrow="Evolución"
              title="Variación de costos"
              description="Seguimiento del comportamiento económico del período según la agrupación correspondiente."
            >
              {barChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={barChartData}>
                    <defs>
                      {Object.entries(PIE_COLORS).map(([name, color], index) => (
                        <linearGradient key={name} id={`color${index}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={color} stopOpacity={0.85} />
                          <stop offset="95%" stopColor={color} stopOpacity={0.18} />
                        </linearGradient>
                      ))}
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <YAxis tickFormatter={(value) => `$${value / 1000}k`} tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip formatter={(value) => `$ ${formatCurrency(value)}`} />
                    <Legend />

                    {Object.keys(PIE_COLORS).map((key, index) => (
                      <Bar key={key} dataKey={key} stackId="a" fill={`url(#color${index})`} radius={[6, 6, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  title="Todavía no hay evolución disponible"
                  description="Cuando el período tenga actividad registrada, vas a poder seguir la evolución desde este gráfico."
                />
              )}
            </ChartShell>
          </div>
        </section>
      )}

      <ChartShell
        eyebrow="Territorio"
        title={`Destinos más frecuentes · ${periodLabel}`}
        description="Paradas con mayor recurrencia dentro del período seleccionado."
      >
        {frequentDestinations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="py-3 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Destino</th>
                  <th className="py-3 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Visitas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {frequentDestinations.map((dest, index) => (
                  <tr key={dest.name} className="hover:bg-slate-50/80">
                    <td className="py-3 px-3 text-sm font-medium text-slate-800">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">
                          {index + 1}
                        </span>
                        <span>{dest.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-sm font-bold text-slate-900">{dest.visits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="Todavía no hay destinos frecuentes"
            description="Cuando el período tenga recorridos resueltos, acá vas a ver cuáles fueron las paradas más repetidas."
          />
        )}
      </ChartShell>
    </div>
  );
}
